import React from "react";
import { CloseIcon } from "@icons";
import { Entity } from "@common/types/entity";
import {
    ConsolidationRelationshipResponse,
    CreateConsolidationRelationshipBody,
    UpdateConsolidationRelationshipBody,
} from "@common/schemas/domains/accounting/consolidation.schema";
import { ConsolidationEntityRelationshipType } from "@common/types/domains/accounting/consolidation";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { HStack } from "@design-system/HStack/HStack";
import { Input } from "@design-system/Input/Input";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { RELATIONSHIP_TYPE_LABELS } from "../ownershipGraph.helpers";
import { PaneSection } from "./PaneSection";
import { useRelationshipForm } from "./useRelationshipForm";
import paneStyles from "./Pane.module.scss";
import styles from "./RelationshipFormPane.module.scss";

const CREATABLE_RELATIONSHIP_TYPES = [
    ConsolidationEntityRelationshipType.ownership,
    ConsolidationEntityRelationshipType.loan,
    ConsolidationEntityRelationshipType.other,
] satisfies ConsolidationEntityRelationshipType[];

const RELATIONSHIP_TYPE_ITEMS: Array<
    PickerItemData<ConsolidationEntityRelationshipType>
> = CREATABLE_RELATIONSHIP_TYPES.map((relationshipType) => ({
    value: relationshipType,
    label: RELATIONSHIP_TYPE_LABELS[relationshipType],
}));

export interface RelationshipFormState {
    mode: "create" | "edit";
    relationship?: ConsolidationRelationshipResponse;
    presetSourceEntityUuid?: string;
    presetTargetEntityUuid?: string;
}

interface RelationshipFormPaneProps {
    formState: RelationshipFormState;
    entities: Entity[];
    onClose: () => void;
    onCreate: (body: CreateConsolidationRelationshipBody) => void;
    onUpdate: (id: string, body: UpdateConsolidationRelationshipBody) => void;
    onDraftEndpointsChange: (endpoints: {
        sourceEntityUuid: string | null;
        targetEntityUuid: string | null;
    }) => void;
    isSaving: boolean;
    errorMessage: string | null;
}

interface FormFieldProps {
    label: string;
    children: React.ReactNode;
}

const FormField: React.FC<FormFieldProps> = ({ label, children }) => (
    <div className={styles.field}>
        <span className={styles.fieldLabel}>{label}</span>
        <div className={styles.fieldControl}>{children}</div>
    </div>
);

export const RelationshipFormPane: React.FC<RelationshipFormPaneProps> = ({
    formState,
    entities,
    onClose,
    onCreate,
    onUpdate,
    onDraftEndpointsChange,
    isSaving,
    errorMessage,
}) => {
    const isEdit = formState.mode === "edit";
    const existing = formState.relationship;

    const {
        type,
        setType,
        sourceEntityUuid,
        setSourceEntityUuid,
        targetEntityUuid,
        setTargetEntityUuid,
        percentage,
        name,
        setName,
        ownershipType,
        setOwnershipType,
        payment,
        setPayment,
        targetBalance,
        setTargetBalance,
        frequency,
        setFrequency,
        notes,
        setNotes,
        partyLabels,
        isOwnership,
        isCashSweep,
        hasPaymentField,
        businessEntities,
        entityName,
        canSubmit,
        handleSubmit,
    } = useRelationshipForm({
        isEdit,
        existing,
        entities,
        onCreate,
        onUpdate,
        presetSourceEntityUuid: formState.presetSourceEntityUuid,
        presetTargetEntityUuid: formState.presetTargetEntityUuid,
    });

    React.useEffect(() => {
        if (!isEdit) {
            onDraftEndpointsChange({
                sourceEntityUuid,
                targetEntityUuid,
            });
        }
    }, [isEdit, onDraftEndpointsChange, sourceEntityUuid, targetEntityUuid]);

    const renderEntitySelect = (
        value: string | null,
        onChange: (uuid: string | null) => void,
        options: Entity[],
    ) => {
        const selectedEntity = options.find(
            ({ uuid: entityUuid }) => entityUuid === value,
        );
        return (
            <Picker.Select
                items={options.map((entity) => ({
                    value: entity.uuid,
                    label: entityName(entity.uuid) ?? "",
                    leading: (
                        <EntityAvatar entity={entity} size="xs" aria-hidden />
                    ),
                }))}
                value={value}
                onValueChange={onChange}
                placeholder="Select entity"
                renderValue={() =>
                    selectedEntity ? (
                        <HStack gap={8} align="center">
                            <EntityAvatar
                                entity={selectedEntity}
                                size="xs"
                                aria-hidden
                            />
                            <span>{entityName(selectedEntity.uuid)}</span>
                        </HStack>
                    ) : undefined
                }
                trigger={
                    <Picker.Button
                        variant="primary"
                        size="md"
                        className={paneStyles.selectTrigger}
                    />
                }
            />
        );
    };

    return (
        <aside className={styles.panel}>
            <div className={styles.header}>
                <span className={styles.headerTitle}>
                    {isEdit ? "Edit relationship" : "Add relationship"}
                </span>
                <Button
                    variant="secondary"
                    size="md"
                    iconOnly
                    onClick={onClose}
                    aria-label="Close relationship form"
                >
                    <CloseIcon />
                </Button>
            </div>
            <div className={styles.body}>
                <div className={styles.topFields}>
                    <FormField label="Type">
                        {isEdit ? (
                            <span className={paneStyles.detailValue}>
                                {RELATIONSHIP_TYPE_LABELS[type]}
                            </span>
                        ) : (
                            <Picker.Select
                                items={RELATIONSHIP_TYPE_ITEMS}
                                value={type}
                                onValueChange={(value) => {
                                    if (value) {
                                        setType(value);
                                    }
                                }}
                                renderValue={() =>
                                    RELATIONSHIP_TYPE_LABELS[type]
                                }
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="md"
                                        className={paneStyles.selectTrigger}
                                    />
                                }
                            />
                        )}
                    </FormField>
                </div>

                <PaneSection title="Properties" defaultExpanded>
                    <FormField label={partyLabels.source}>
                        {isEdit ? (
                            <span className={paneStyles.detailValue}>
                                {entityName(sourceEntityUuid) ?? "Unknown"}
                            </span>
                        ) : (
                            renderEntitySelect(
                                sourceEntityUuid,
                                setSourceEntityUuid,
                                entities,
                            )
                        )}
                    </FormField>
                    <FormField label={partyLabels.target}>
                        {isEdit ? (
                            <span className={paneStyles.detailValue}>
                                {entityName(targetEntityUuid) ?? "Unknown"}
                            </span>
                        ) : (
                            renderEntitySelect(
                                targetEntityUuid,
                                setTargetEntityUuid,
                                businessEntities,
                            )
                        )}
                    </FormField>
                    {isOwnership && (
                        <FormField label="Ownership">
                            <span className={paneStyles.detailValue}>
                                {percentage}%
                            </span>
                        </FormField>
                    )}
                    <FormField label="Label">
                        <Input
                            variant="ghost"
                            className={styles.inlineInput}
                            size="md"
                            fontSize={12}
                            placeholder="Optional"
                            maxLength={200}
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />
                    </FormField>
                    {isOwnership && (
                        <FormField label="Ownership type">
                            <Input
                                variant="ghost"
                                className={styles.inlineInput}
                                size="md"
                                fontSize={12}
                                placeholder="Common"
                                maxLength={100}
                                value={ownershipType}
                                onChange={(event) =>
                                    setOwnershipType(event.target.value)
                                }
                            />
                        </FormField>
                    )}
                    {hasPaymentField && (
                        <FormField label="Payment">
                            <Input
                                variant="ghost"
                                className={styles.inlineInput}
                                size="md"
                                fontSize={12}
                                placeholder="$2k/mo"
                                maxLength={100}
                                value={payment}
                                onChange={(event) =>
                                    setPayment(event.target.value)
                                }
                            />
                        </FormField>
                    )}
                    {isCashSweep && (
                        <>
                            <FormField label="Target balance">
                                <Input
                                    variant="ghost"
                                    className={styles.inlineInput}
                                    size="md"
                                    fontSize={12}
                                    placeholder="$0"
                                    maxLength={100}
                                    value={targetBalance}
                                    onChange={(event) =>
                                        setTargetBalance(event.target.value)
                                    }
                                />
                            </FormField>
                            <FormField label="Frequency">
                                <Input
                                    variant="ghost"
                                    className={styles.inlineInput}
                                    size="md"
                                    fontSize={12}
                                    placeholder="Monthly"
                                    maxLength={100}
                                    value={frequency}
                                    onChange={(event) =>
                                        setFrequency(event.target.value)
                                    }
                                />
                            </FormField>
                        </>
                    )}
                    <FormField label="Notes">
                        <Input
                            variant="ghost"
                            className={styles.inlineInput}
                            size="md"
                            fontSize={12}
                            placeholder="Optional"
                            maxLength={2000}
                            value={notes}
                            onChange={(event) => setNotes(event.target.value)}
                        />
                    </FormField>
                </PaneSection>

                {errorMessage && (
                    <div className={styles.error}>{errorMessage}</div>
                )}
            </div>
            <div className={styles.footer}>
                <WithHotkey
                    hotKey="mod+enter"
                    hotKeyDisplayPosition="inline"
                    hotKeyOptions={{
                        description: isEdit
                            ? "Save relationship"
                            : "Create relationship",
                        preventDefault: true,
                        enableOnFormTags: true,
                    }}
                >
                    {(hotkeyHint) => (
                        <Button
                            variant="primary"
                            size="md"
                            block
                            disabled={!canSubmit || isSaving}
                            onClick={handleSubmit}
                            rightIcon={hotkeyHint}
                        >
                            {isEdit ? "Save" : "Create"}
                        </Button>
                    )}
                </WithHotkey>
            </div>
        </aside>
    );
};
